import {
  URL_ADAF,
  URL_ADDRESS_PAGE,
  URL_USER_OFFERS,
  URL_USER_ORGANIZATION_TEAM,
  URL_SUBSCRIPTION,
  URL_USER_NOTIFICATIONS,
  URL_USER_PROFILE_MANAGE,
  URL_OFFER_QUESTION,
} from '@tools/routes/routes';
import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useModal } from '@composables/useModal.ts';
import type { Url } from '@global-types/url';
import { useAppDataStore } from '@stores/useAppDataStore';
import { eventBus } from '@tools/event-bus';
import { SHOW_LOGIN_MODAL } from '@molecules/modal-constants';
import type { ContactLinks } from '@global-types/nav-v2/contactLinks';
import { PhoneNumber } from '@tools/phoneNumber.ts';
import type { Locations } from '@global-types/nav-v2/locations';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';

export const useNavbarStore = defineStore('navbar', () => {
  const {
    userNotifications: notifications,
    navbarData,
  } = useAppDataStore();

  const { isMobile } = storeToRefs(useScreensizeStore());

  const urlExpanded: string[] = [
    URL_USER_NOTIFICATIONS,
    URL_USER_PROFILE_MANAGE,
    URL_USER_OFFERS,
    URL_SUBSCRIPTION,
    URL_ADAF,
    URL_ADDRESS_PAGE,
    URL_USER_ORGANIZATION_TEAM,
  ] as const;

  const urlExpandedPatterns: RegExp[] = [
    /^\/spaces\/\d+$/,
    /^\/organization\/business_input\/\d{6}$/,
  ] as const;

  const enableScrollHidePatterns: RegExp[] = [
    /^\/location-bureau-[^/?#]+\/?$/i, // Landing page offices
    /^\/coworking-[^/?#]+\/?$/i, // Landing page coworking
    /^\/recherche(?:\/)?\?.+/i, // Search page
  ];

  const isExpanded = ref<boolean>(false);
  const currentDropdownOpen = ref<string | null>(null);

  const setCurrentDropdownOpen = (newCurrentDropdownOpen: string | null): void => {
    currentDropdownOpen.value = newCurrentDropdownOpen;
  };

  const toggleExpanded = (): void => {
    isExpanded.value = !isExpanded.value;

    setCurrentDropdownOpen(null);
  };

  const currentPathname = ref<string>(window.location.pathname);
  const isCurrentPage = (link: string | undefined): boolean => currentPathname.value === link;

  const setCurrentPathname = (pathname: string): void => {
    currentPathname.value = pathname;
  };

  window.addEventListener('popstate', () => {
    currentPathname.value = window.location.pathname;
  });

  const {
    isOpen: isPlusMenuOpen,
    toggleModal: togglePlusMenu,
    closeModal: closePlusMenu,
  } = useModal();

  const isCurrentDropdownOpen = (dropdown: string): boolean => currentDropdownOpen.value === dropdown;

  const hiddenMobileNavbarRoutes: string[] = [
    URL_OFFER_QUESTION,
  ] as const;

  const hiddenMobileNavbarRoutePatterns: RegExp[] = [
    /^\/O-[A-Z0-9]{6}(?:-[^/]+)*\/?$/, // offer page, regex for hash_id - name
    /^\/offer\/[^/]+\/[^/]+\/completion\/\d+\/?$/, // first step offer contact
    /^\/offer\/[^/]+\/[^/]+\/schedule\/\d+\/?$/, // second step offer contact
    /^\/profile\/deal\/\d+\/messaging\/?$/, // deal messaging page
  ] as const;

  const isMobileNavbarVisible = ref<boolean>(true);

  const updateNavbarMobileIsVisible = (): void => {
    if (!isMobile.value) {
      isMobileNavbarVisible.value = true;
      return;
    }

    const { pathname } = window.location;

    const isHiddenExact = hiddenMobileNavbarRoutes.includes(pathname);
    const isHiddenPattern = hiddenMobileNavbarRoutePatterns.some((pattern) => pattern.test(pathname));

    isMobileNavbarVisible.value = !(isHiddenExact || isHiddenPattern);
  };

  updateNavbarMobileIsVisible();

  const shouldEnableScrollHide = (): boolean => {
    const { pathname, search } = window.location;
    const fullPath = `${pathname}${search}`;
    return enableScrollHidePatterns.some((pattern) => pattern.test(fullPath));
  };

  const setExpanded = (expanded: boolean): void => {
    isExpanded.value = expanded;
  };

  const initExpanded = (): void => {
    const { pathname } = window.location;

    const isExactMatch = urlExpanded.includes(pathname);
    const isPatternMatch = urlExpandedPatterns.some((pattern) => pattern.test(pathname));

    if (isExactMatch || isPatternMatch) {
      setExpanded(true);
    }
  };

  initExpanded();

  const contactLinks = ref<ContactLinks>(navbarData?.contact_links as ContactLinks);

  const whatsappLink = computed<Url | null>(() => contactLinks.value.whatsappLink);
  const faqLink = computed<Url | null>(() => contactLinks.value.intercomFaqLink);
  const calendlyUrl = computed<Url | null>(() => contactLinks.value.calendlyUrl);
  const salesPhone = computed<string | null>(() => contactLinks.value.salesPhone);
  const bookAppointmentUrl = computed<Url | null>(() => contactLinks.value.bookAppointmentUrl);

  const {
    isOpen: isTopPanelOpen,
    toggleModal: toggleTopPanel,
    closeModal: closeTopPanel,
  } = useModal();

  const openLoginModal = (): void => {
    eventBus.dispatchEvent(new CustomEvent(SHOW_LOGIN_MODAL));
  };

  const openRegisterModal = (): void => {
    eventBus.dispatchEvent(new CustomEvent('show-register-choice-modal', { detail: window.location.href }));
  };

  const internationalSalesPhone = computed<string | null>(() => {
    if (!salesPhone.value) {
      return null;
    }

    const internationalFormattedPhone = (new PhoneNumber(salesPhone.value)).printFormattedInternational;

    if (!internationalFormattedPhone) {
      return null;
    }

    return internationalFormattedPhone.replace(/ /g, '');
  });

  const locations = computed<Locations>(() => navbarData?.locations as Locations);

  const parisDistricts = computed<string[]>(() => locations.value.paris.districts);
  const parisPois = computed<string[]>(() => locations.value.paris.poi);
  const parisAreaUrl = computed<Url>(() => locations.value.paris.area.url);

  const franceCities = computed<string[]>(() => locations.value.france.cities);
  const franceAreaUrl = computed<Url>(() => locations.value.france.area.url);

  const idfCities = computed<string[]>(() => locations.value.idf.cities);
  const idfAreaUrl = computed<Url>(() => locations.value.idf.area.url);

  const plusCoworkingParisUrl = computed<Url>(() => 'coworking-paris');
  const plusPriceParisUrl = computed<Url>(() => 'prix-bureaux-paris');

  return {
    isExpanded,
    setExpanded,
    toggleExpanded,
    isCurrentPage,
    notifications,
    isPlusMenuOpen,
    togglePlusMenu,
    closePlusMenu,
    currentDropdownOpen,
    setCurrentDropdownOpen,
    isCurrentDropdownOpen,
    isMobileNavbarVisible,
    shouldEnableScrollHide,
    updateNavbarMobileIsVisible,
    whatsappLink,
    faqLink,
    calendlyUrl,
    salesPhone,
    internationalSalesPhone,
    bookAppointmentUrl,
    isTopPanelOpen,
    toggleTopPanel,
    openLoginModal,
    openRegisterModal,
    closeTopPanel,
    parisDistricts,
    parisPois,
    parisAreaUrl,
    franceCities,
    franceAreaUrl,
    idfCities,
    idfAreaUrl,
    plusCoworkingParisUrl,
    plusPriceParisUrl,
    currentPathname,
    setCurrentPathname,
  };
});
